import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from './PageHeader';
import { Target, BookOpen, Sparkles, Repeat, ChevronRight, ArrowRight, Pause } from './Icons';
import useHaptics from '../hooks/useHaptics';
import { apiGet, apiPost } from '../lib/api';
import './Focus.css';

const PRESETS = [
  { id: 'deep_work', name: 'Deep Work', minutes: 50, Icon: Target },
  { id: 'learn',     name: 'Learn',     minutes: 30, Icon: BookOpen },
  { id: 'create',    name: 'Create',    minutes: 45, Icon: Sparkles },
  { id: 'reset',     name: 'Reset',     minutes: 10, Icon: Repeat },
];

const DEFAULT_TITLE = 'Focus session';

const formatClock = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

export default function Focus({ userId } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { trigger } = useHaptics();

  const incomingActivityId = location.state?.activityId || null;

  const [selectedPreset, setSelectedPreset] = useState('deep_work');
  const [upcoming, setUpcoming] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [paused, setPaused] = useState(false);
  const [doneFlash, setDoneFlash] = useState(false);
  const [activityTitle, setActivityTitle] = useState(null);
  const [activityMinutes, setActivityMinutes] = useState(null);
  const [titleLoading, setTitleLoading] = useState(false);

  const intervalRef = useRef(null);
  const doneTimeoutRef = useRef(null);
  const sessionRef = useRef(null);
  const mountedRef = useRef(true);
  const startedAtRef = useRef(null);     // wall-clock ms when current run started
  const targetMsRef = useRef(0);          // total session duration in ms
  const elapsedAtPauseRef = useRef(0);    // ms elapsed accumulated before pause

  useEffect(() => { sessionRef.current = activeSession; }, [activeSession]);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const preset = useMemo(
    () => PRESETS.find((p) => p.id === selectedPreset) || PRESETS[0],
    [selectedPreset]
  );

  // Resolve session title: prefer the activity passed in via router state,
  // fall back to the preset name. Updates if user re-enters with a new id.
  useEffect(() => {
    if (!incomingActivityId || !userId) {
      setActivityTitle(null);
      setActivityMinutes(null);
      return;
    }
    let cancelled = false;
    setTitleLoading(true);
    (async () => {
      try {
        const res = await apiGet(`/api/activities/detail/${userId}/${incomingActivityId}`);
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        if (cancelled || !data) return;
        const name = data.name || data.title;
        if (name) setActivityTitle(name);
        const m = data.duration_minutes || data.target_minutes;
        if (typeof m === 'number' && m > 0) setActivityMinutes(m);
      } catch (_) { /* fall through to preset */ }
      finally { if (!cancelled) setTitleLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [incomingActivityId, userId]);

  const sessionTitle = titleLoading ? 'Loading…' : (activityTitle || `${preset.name} session`);
  const sessionMinutes = activityMinutes || preset.minutes;

  // Load upcoming sessions (silent fail — empty list is honest)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/api/focus/sessions/upcoming');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.slice(0, 3).map((s, i) => {
            let time = s.time || s.start_time || s.preferred_time;
            if (!time && s.started_at) {
              try {
                const d = new Date(s.started_at);
                time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
              } catch (_) { /* ignore */ }
            }
            return {
              id: s.id || s.session_id || `s-${i}`,
              activity_id: s.activity_id || null,
              time: time || '—',
              title: s.title || DEFAULT_TITLE,
              meta: s.meta || `${s.target_minutes || 30} min`,
            };
          });
          setUpcoming(mapped);
        }
      } catch (e) { /* keep empty */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const handleSessionComplete = useCallback(async () => {
    trigger('success');
    setDoneFlash(true);
    const sid = sessionRef.current?.id;
    if (sid) {
      try {
        await apiPost(`/api/focus/sessions/${sid}/end`, { interruptions: 0 });
      } catch (e) { /* ignore */ }
    }
    // Notify other views (Today, Plans, Insights) that logs may have changed.
    try {
      window.dispatchEvent(new CustomEvent('hx:logs-changed'));
    } catch (_) { /* non-DOM env */ }
    if (!mountedRef.current) return;
    doneTimeoutRef.current = setTimeout(() => {
      setActiveSession(null);
      setRemaining(0);
      setPaused(false);
      setDoneFlash(false);
    }, 1500);
  }, [trigger]);

  // Tick down active session — wall-clock based to avoid setInterval drift.
  useEffect(() => {
    if (!activeSession) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    if (paused) {
      // Snapshot how much we've used so far, then stop ticking.
      if (startedAtRef.current != null) {
        elapsedAtPauseRef.current += Date.now() - startedAtRef.current;
        startedAtRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    // Resuming (or starting): re-anchor the start timestamp.
    if (startedAtRef.current == null) {
      startedAtRef.current = Date.now();
    }
    const tick = () => {
      const elapsed = elapsedAtPauseRef.current + (Date.now() - startedAtRef.current);
      const remainingMs = Math.max(0, targetMsRef.current - elapsed);
      const remainingSec = Math.round(remainingMs / 1000);
      setRemaining(remainingSec);
      if (remainingMs <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        handleSessionComplete();
      }
    };
    tick(); // immediate sync so paused→resumed feels instant
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [activeSession, paused, handleSessionComplete]);

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (doneTimeoutRef.current) clearTimeout(doneTimeoutRef.current);
  }, []);

  const handleStart = useCallback(async () => {
    if (activeSession) return;
    trigger('medium');
    const targetMinutes = sessionMinutes;
    let session = { id: `local-${Date.now()}`, target_minutes: targetMinutes, title: sessionTitle };
    try {
      const res = await apiPost('/api/focus/sessions', {
        preset: preset.id,
        title: sessionTitle,
        target_minutes: targetMinutes,
        activity_id: incomingActivityId || undefined,
      });
      if (res.ok) {
        const data = await res.json().catch(() => null);
        if (data && data.id) session = { ...session, ...data };
      }
    } catch (e) { /* fall back to local session */ }
    if (!mountedRef.current) return;
    targetMsRef.current = targetMinutes * 60 * 1000;
    elapsedAtPauseRef.current = 0;
    startedAtRef.current = Date.now();
    setActiveSession(session);
    setRemaining(targetMinutes * 60);
    setPaused(false);
  }, [activeSession, preset, trigger, sessionTitle, sessionMinutes, incomingActivityId]);

  const handlePauseToggle = useCallback(() => {
    trigger('light');
    setPaused((p) => !p);
  }, [trigger]);

  const handleEnd = useCallback(async () => {
    trigger('medium');
    const sid = activeSession?.id;
    if (sid) {
      try {
        await apiPost(`/api/focus/sessions/${sid}/end`, { interruptions: 0 });
      } catch (e) { /* ignore */ }
    }
    if (!mountedRef.current) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    startedAtRef.current = null;
    elapsedAtPauseRef.current = 0;
    targetMsRef.current = 0;
    setActiveSession(null);
    setRemaining(0);
    setPaused(false);
  }, [activeSession, trigger]);

  const handleSelectPreset = useCallback((id) => {
    if (activeSession) return;
    trigger('light');
    setSelectedPreset(id);
  }, [activeSession, trigger]);

  const handleRowClick = useCallback((row) => {
    trigger('light');
    if (row.activity_id) {
      navigate('/app/focus', { state: { activityId: row.activity_id } });
    } else {
      navigate('/app/plans');
    }
  }, [navigate, trigger]);

  const targetSeconds = (activeSession?.target_minutes || sessionMinutes) * 60;
  const progress = activeSession
    ? Math.max(0, Math.min(1, 1 - remaining / targetSeconds))
    : 0;

  return (
    <div className="page focus-page">
      <PageHeader />

      <header className="focus-title-block stagger">
        <h1 className="h-display">Focus</h1>
        <p className="subtitle">Protect your best work.</p>
      </header>

      <div className="stagger focus-sections">
        {activeSession ? (
          <section className="current-session black-card running" aria-live="polite">
            <div className="eyebrow-gold session-eyebrow">
              <span className="eyebrow-dot" /> {doneFlash ? 'DONE' : 'IN FOCUS'}
            </div>
            <h2 className="h-2 session-title">{activeSession.title || sessionTitle}</h2>
            <div className={`running-timer mono${paused ? ' is-paused' : ''}`}>{formatClock(remaining)}</div>
            {paused && <div className="running-paused-badge">PAUSED</div>}
            <div className="running-progress" aria-hidden>
              <div className="running-progress-bar" style={{ width: `${progress * 100}%` }} />
            </div>
            <div className="running-actions">
              <button type="button" className="btn-outline running-btn" onClick={handlePauseToggle} aria-label={paused ? 'Resume' : 'Pause'}>
                <Pause size={16} /><span>{paused ? 'Resume' : 'Pause'}</span>
              </button>
              <button type="button" className="btn-primary running-btn" onClick={handleEnd} aria-label="End session">End</button>
            </div>
          </section>
        ) : (
          <section className="current-session black-card">
            <div className="eyebrow-gold session-eyebrow">
              <span className="eyebrow-dot" /> CURRENT SESSION
            </div>
            <h2 className="h-2 session-title">{sessionTitle}</h2>
            <div className="meta-pills" role="list">
              <span className="meta-pill" role="listitem">
                <ClockIcon /> {sessionMinutes} min
              </span>
            </div>
            <button type="button" className="btn-primary start-focus-btn" onClick={handleStart} aria-label="Start focus session">
              <span>Start Focus</span><ArrowRight size={18} />
            </button>
          </section>
        )}

        {!incomingActivityId && (
          <section className="focus-section">
            <div className="section-head">
              <span className="eyebrow">Session Presets</span>
            </div>
            <div className="preset-grid">
              {PRESETS.map(({ id, name, minutes, Icon }) => {
                const active = id === selectedPreset;
                return (
                  <button key={id} type="button" className={`preset-tile ${active ? 'is-active' : ''}`} onClick={() => handleSelectPreset(id)} aria-pressed={active} aria-label={`${name}, ${minutes} minutes`}>
                    {active && <span className="preset-dot" aria-hidden />}
                    <Icon size={18} />
                    <span className="preset-name">{name}</span>
                    <span className="eyebrow preset-sub">{minutes} min</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {upcoming.length > 0 && (
          <section className="focus-section">
            <div className="section-head">
              <span className="eyebrow">Upcoming Sessions</span>
            </div>
            <ul className="upcoming-list" role="list">
              {upcoming.map((row, i) => (
                <li key={row.id} className={`upcoming-row ${i === upcoming.length - 1 ? 'is-last' : ''}`}>
                  <button type="button" className="upcoming-row-btn" onClick={() => handleRowClick(row)} aria-label={`Open plans: ${row.title}`}>
                    <span className="row-time mono">{row.time}</span>
                    <span className="row-content">
                      <span className="row-title">{row.title}</span>
                      <span className="eyebrow row-meta">{row.meta}</span>
                    </span>
                    <ChevronRight size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

// Tiny inline icon for the duration pill (kept local to avoid bloating Icons.js)
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

